/**
 * Config-scope schemas — the server replacement for the desktop's
 * `app-state.json` plugin-store (ARCHITECTURE §3 "Config model",
 * docs/inventory/04-sources-lifecycle.md §7 persisted settings groups).
 *
 * Every persisted settings group of the desktop app is mirrored here as a
 * zod schema, and `CONFIG_KEYS` maps each key to its allowed scopes
 * (tenant / user / project), its schema, and whether its value is
 * secret-bearing (encrypted at rest in `configs.enc`, AES-256-GCM).
 *
 * Value shapes mirror `vendor/upstream/src/stores/wiki-store.ts` exactly —
 * the engine's plugin-store adapter reads/writes these keys verbatim, so
 * any drift breaks the vendored engine.
 *
 * Deliberate drops vs desktop (recorded in ARCHITECTURE §18):
 *  - `generalConfig` (autostart/closeBehavior), `updateCheckState`,
 *    `zoomLevel` desktop semantics (kept as a plain user pref),
 *    `apiConfig.allowUnauthenticated` (meaningless off-localhost).
 *  - AnyTXT (F-099) is dropped for v1; the schema keeps the field so
 *    desktop-project imports parse, but the surface is hidden.
 */
import { z } from "zod";
import { EpochMsSchema, UuidSchema } from "./core.js";
// ─── LLM provider config (wiki-store.ts LlmConfig) ───────────────────────
export const LlmProviderSchema = z.enum([
    "openai",
    "anthropic",
    "google",
    "azure",
    "ollama",
    "custom",
    "minimax",
    "claude-code",
    "codex-cli",
    // First-class OpenRouter provider registered by the engine patch
    // packages/engine/patches/0001-llm-providers-openrouter.patch.
    "openrouter",
]);
export const CustomApiModeSchema = z.enum([
    "chat_completions",
    "anthropic_messages",
]);
export const AzureModelFamilySchema = z.enum(["auto", "gpt5"]);
export const ReasoningModeSchema = z.enum([
    "auto",
    "off",
    "low",
    "medium",
    "high",
    "max",
    "custom",
]);
export const ReasoningConfigSchema = z.object({
    mode: ReasoningModeSchema,
    budgetTokens: z.number().int().positive().optional(),
});
export const LlmConfigSchema = z.object({
    provider: LlmProviderSchema,
    apiKey: z.string(),
    model: z.string(),
    ollamaUrl: z.string(),
    customEndpoint: z.string(),
    azureApiVersion: z.string().optional(),
    azureModelFamily: AzureModelFamilySchema.optional(),
    /** Max context window in characters. */
    maxContextSize: z.number().int().positive(),
    apiMode: CustomApiModeSchema.optional(),
    reasoning: ReasoningConfigSchema.optional(),
    localCliIsolation: z.boolean().optional(),
    codexCliTimeoutMinutes: z.number().int().min(1).max(240).optional(),
});
/** Per-preset saved overrides (wiki-store.ts ProviderOverride). */
export const ProviderOverrideSchema = z.object({
    apiKey: z.string().optional(),
    model: z.string().optional(),
    /** customEndpoint for custom presets, ollamaUrl for ollama. */
    baseUrl: z.string().optional(),
    azureApiVersion: z.string().optional(),
    azureModelFamily: AzureModelFamilySchema.optional(),
    apiMode: CustomApiModeSchema.optional(),
    maxContextSize: z.number().int().positive().optional(),
    reasoning: ReasoningConfigSchema.optional(),
    localCliIsolation: z.boolean().optional(),
    codexCliTimeoutMinutes: z.number().int().min(1).max(240).optional(),
});
export const ProviderConfigsSchema = z.record(ProviderOverrideSchema);
/**
 * Chat-only override of the LLM used for a single chat turn (dashboard model
 * selector). Picks provider + model WITHOUT mutating the project's stored
 * llmConfig — which also drives ingest — so the choice never leaks outside
 * the chat. The runtime resolves the API key (and endpoint/context override)
 * from `providerConfigs[provider]` at send time; a provider with no stored
 * key is rejected with a clear error.
 */
export const ModelOverrideSchema = z.object({
    provider: LlmProviderSchema,
    model: z.string().min(1),
});
// ─── Web-search config (wiki-store.ts SearchApiConfig) ───────────────────
export const SearchProviderSchema = z.enum([
    "tavily",
    "serpapi",
    "searxng",
    "ollama",
    "none",
]);
export const DeepResearchSourceSchema = z.enum(["web", "anytxt", "both"]);
/** Open string sets upstream (`| string`), so plain strings on the wire. */
export const SerpApiEngineSchema = z.string();
export const SearXngCategorySchema = z.string();
export const SearchProviderOverrideSchema = z.object({
    apiKey: z.string().optional(),
    serpApiEngine: SerpApiEngineSchema.optional(),
    searXngUrl: z.string().optional(),
    searXngCategories: z.array(SearXngCategorySchema).optional(),
    ollamaUrl: z.string().optional(),
});
/** AnyTXT is DROPPED for v1 (F-099); schema kept for desktop-import parse. */
export const AnyTxtConfigSchema = z.object({
    enabled: z.boolean().optional(),
    endpoint: z.string().optional(),
    filterDir: z.string().optional(),
    filterExt: z.string().optional(),
    limit: z.number().int().positive().optional(),
});
export const SearchApiConfigSchema = z.object({
    provider: SearchProviderSchema,
    apiKey: z.string(),
    serpApiEngine: SerpApiEngineSchema.optional(),
    searXngUrl: z.string().optional(),
    searXngCategories: z.array(SearXngCategorySchema).optional(),
    ollamaUrl: z.string().optional(),
    providerConfigs: z
        .record(SearchProviderOverrideSchema)
        .optional(),
    deepResearchSource: DeepResearchSourceSchema.optional(),
    anyTxt: AnyTxtConfigSchema.optional(),
});
// ─── Embedding config (wiki-store.ts EmbeddingConfig) ────────────────────
export const EmbeddingConfigSchema = z.object({
    enabled: z.boolean(),
    endpoint: z.string(),
    apiKey: z.string(),
    model: z.string(),
    outputDimensionality: z.number().int().positive().optional(),
    maxChunkChars: z.number().int().positive().optional(),
    overlapChunkChars: z.number().int().nonnegative().optional(),
    extraHeaders: z.record(z.string()).optional(),
    /**
     * [0017] Corsia pesante: endpoint/modello usati SOLO dal worker di
     * indicizzazione pagine (bulk). Le query di ricerca restano su
     * `endpoint`. Serve a tenere la CPU per l'interattivo e mandare
     * l'embedding delle pagine sulla GPU (che puo attendere lo scheduling
     * Slurm, cosa inaccettabile per una query utente). Assenti = un solo
     * endpoint per tutto, comportamento storico.
     */
    workerEndpoint: z.string().optional(),
    workerModel: z.string().optional(),
});
// ─── Multimodal / vision captioning (wiki-store.ts MultimodalConfig) ─────
export const MultimodalConfigSchema = z.object({
    enabled: z.boolean(),
    useMainLlm: z.boolean(),
    provider: LlmProviderSchema,
    apiKey: z.string(),
    model: z.string(),
    ollamaUrl: z.string(),
    customEndpoint: z.string(),
    azureApiVersion: z.string().optional(),
    azureModelFamily: AzureModelFamilySchema.optional(),
    apiMode: CustomApiModeSchema.optional(),
    /** Max parallel caption requests during ingest. >=1. */
    concurrency: z.number().int().min(1),
});
// ─── MinerU (wiki-store.ts MineruConfig) ─────────────────────────────────
export const MineruModelVersionSchema = z.enum(["pipeline", "vlm"]);
export const MineruConfigSchema = z.object({
    enabled: z.boolean(),
    token: z.string(),
    modelVersion: MineruModelVersionSchema,
});
// ─── Outbound proxy (wiki-store.ts ProxyConfig) ──────────────────────────
/**
 * Per-tenant outbound proxy. Desktop applied this via process env vars;
 * server-side it becomes a per-request undici ProxyAgent /
 * reqwest::Proxy (coupling §12; process env cannot be per-tenant).
 */
export const ProxyConfigSchema = z.object({
    enabled: z.boolean(),
    url: z.string(),
    bypassLocal: z.boolean(),
});
// ─── Scheduled import (wiki-store.ts ScheduledImportConfig) ──────────────
export const ScheduledImportConfigSchema = z.object({
    enabled: z.boolean(),
    /** Watched dir, relative to project root; `""` = default `raw`. */
    path: z.string(),
    /** Scan interval in minutes (engine clamps to 1..1440). */
    interval: z.number().int().min(1).max(1440),
    lastScan: EpochMsSchema.nullable(),
});
// ─── Source watch (wiki-store.ts SourceWatchConfig + defaults json) ──────
export const SourceWatchConfigSchema = z.object({
    enabled: z.boolean(),
    autoIngest: z.boolean(),
    includeExtensions: z.array(z.string()),
    excludeExtensions: z.array(z.string()),
    excludeDirs: z.array(z.string()),
    excludeGlobs: z.array(z.string()),
    maxFileSizeMb: z.number().int().min(1).max(4096),
});
/**
 * Compiled-in defaults — byte-equal to
 * vendor/upstream/src/lib/source-watch-defaults.json (single source of
 * truth shared between the vendored TS and Rust; keep in lockstep on
 * every vendor-sync).
 */
export const DEFAULT_SOURCE_WATCH_CONFIG = {
    enabled: true,
    autoIngest: true,
    includeExtensions: [
        "md", "mdx", "txt", "pdf", "doc", "docx", "pptx", "xls", "xlsx",
        "odt", "odp", "ods", "rtf", "html", "htm", "csv",
    ],
    excludeExtensions: [
        "tmp", "temp", "bak", "swp", "part", "partial", "crdownload",
        "exe", "dll", "so", "dylib", "bin", "iso", "dmg",
    ],
    excludeDirs: [
        ".git", ".svn", ".hg", ".obsidian", ".idea", ".vscode",
        "node_modules", ".cache", "__pycache__",
    ],
    excludeGlobs: ["~$*", ".~lock.*#", "*.draft.*", "draft-*", "*.private.*"],
    maxFileSizeMb: 100,
};
/**
 * Watcher mode per project — `watch` (inotify) or `scheduled-rescan`
 * (rustd snapshot-diff on an interval; NFS/EFS fallback, graft G-6,
 * ARCHITECTURE §8.3). Server extension — no desktop equivalent.
 */
export const WatcherModeConfigSchema = z.object({
    mode: z.enum(["watch", "scheduled-rescan"]),
    /** Rescan interval seconds when mode = scheduled-rescan (default 60). */
    rescanIntervalSeconds: z.number().int().min(5).max(3600).default(60),
});
// ─── Git source connector (server extension; no desktop equivalent) ──────
/**
 * A remote git repository treated as a raw-document source, alongside the
 * desktop upload / folder-import / watcher inputs (ARCHITECTURE — source
 * connectors, docs/inventory/04-sources-lifecycle.md §3/§5). A sync job
 * in the per-project runtime shallow-clones/pulls the repo and materializes
 * the selected files into `raw/sources/<repo>/`; the EXISTING watcher +
 * ingest-cache (SHA-256) then handle incremental ingest and delete-cascade
 * (zero new ingest logic).
 */
export const GitAuthModeSchema = z.enum(["none", "token", "ssh-key"]);
export const GitSyncModeSchema = z.enum(["manual", "interval"]);
/** Live per-repo sync state (mirror of the last `GitSyncResult`). */
export const GitSyncStatusSchema = z.enum(["idle", "syncing", "ok", "error"]);
export const GitRepoSourceSchema = z.object({
    /** Stable connector id (also the secret-map ref + mirror dir key). */
    id: z.string().min(1).max(200),
    /** Clone URL (https token auth supported; ssh DEFERRED). User-controlled. */
    url: z.string().min(1),
    /** Branch to track; omitted = remote HEAD. */
    branch: z.string().min(1).optional(),
    /** Sync only this subpath of the repo (repo-relative, no `..`). */
    subdir: z.string().optional(),
    /** If non-empty, a file must match at least one glob to be synced. */
    includeGlobs: z.array(z.string()).optional(),
    /** A file matching any of these globs is skipped. */
    excludeGlobs: z.array(z.string()).optional(),
    /**
     * Names the encrypted secret holding the auth token — a key in the
     * project-scoped `gitSourceSecrets` map (NEVER the plaintext token).
     */
    authTokenRef: z.string().optional(),
    auth: GitAuthModeSchema.default("none"),
    syncMode: GitSyncModeSchema.default("manual"),
    /** Interval-mode poll period (engine clamps to 1..1440). */
    intervalMinutes: z.number().int().min(1).max(1440).optional(),
    /** Populated by the runtime after each sync (status mirror). */
    lastSyncedAt: EpochMsSchema.nullable().optional(),
    lastSyncCommit: z.string().nullable().optional(),
    lastError: z.string().nullable().optional(),
});
export const GitSourceConfigSchema = z.object({
    enabled: z.boolean().default(false),
    repos: z.array(GitRepoSourceSchema).default([]),
});
export const DEFAULT_GIT_SOURCE_CONFIG = {
    enabled: false,
    repos: [],
};
/**
 * Encrypted-at-rest map of `authTokenRef` → plaintext auth token
 * (AES-256-GCM in `configs.enc`, same path as `llmConfig`). Only ever
 * decrypted inside the per-project runtime for the duration of a clone/
 * fetch; the token is injected via a git askpass env var and never
 * written to disk, argv, or logs.
 */
export const GitSourceSecretsSchema = z.record(z.string());
/** Result of one repo sync (mirrored into a `git-sync` job + returned by RPC). */
export const GitSyncResultSchema = z.object({
    repoId: z.string(),
    status: GitSyncStatusSchema,
    filesAdded: z.number().int().nonnegative(),
    filesUpdated: z.number().int().nonnegative(),
    filesRemoved: z.number().int().nonnegative(),
    /** HEAD commit synced, or null when the clone/fetch failed. */
    commit: z.string().nullable(),
    durationMs: z.number().int().nonnegative(),
    error: z.string().nullable(),
});
/** Per-repo status row for `gitSource.status`. */
export const GitRepoStatusSchema = z.object({
    id: z.string(),
    url: z.string(),
    syncMode: GitSyncModeSchema,
    intervalMinutes: z.number().int().nullable(),
    lastSyncedAt: EpochMsSchema.nullable(),
    lastSyncCommit: z.string().nullable(),
    lastError: z.string().nullable(),
    status: GitSyncStatusSchema,
});
// ─── Output language / UI prefs ──────────────────────────────────────────
export const OutputLanguageSchema = z.enum([
    "auto",
    "English",
    "Chinese",
    "Traditional Chinese",
    "Japanese",
    "Korean",
    "Vietnamese",
    "French",
    "German",
    "Spanish",
    "Portuguese",
    "Italian",
    "Russian",
    "Arabic",
    "Persian",
    "Hindi",
    "Turkish",
    "Dutch",
    "Polish",
    "Swedish",
    "Indonesian",
    "Thai",
    "Ukrainian",
]);
export const ThemeSchema = z.enum(["light", "dark", "system"]);
/** UI language (i18n locale id, e.g. "en", "zh"). */
export const UiLanguageSchema = z.string().min(2);
export const ZoomLevelSchema = z.number().min(0.5).max(2);
// ─── Tenant-level service flags (server replacements for apiConfig) ──────
/**
 * Desktop `apiConfig` becomes: per-user API tokens (api_tokens table),
 * a per-tenant `apiEnabled` kill-switch, and `mcpEnabled`
 * surfaced in `/health` (ARCHITECTURE §3).
 * `allowUnauthenticated` drops.
 *
 * `mcpEnabled` ha DUE scope e non si combinano per override, ma in AND:
 *
 *     effettivo = tenant && (progetto ?? true)
 *
 * Il valore di tenant resta un kill-switch: se un progetto potesse riaccendere
 * cio che il tenant ha spento, non sarebbe piu un kill-switch. Un progetto puo
 * quindi solo restringere, mai allargare.
 *
 * Il default `true` a livello di progetto (cioe: assente = eredita il tenant) e
 * scelto perche il contrario spegnerebbe l'MCP di ogni wiki gia servita nel
 * momento esatto in cui questo codice va in produzione — una riga di config
 * che diventa un'interruzione di servizio silenziosa. Chi vuole escludere un
 * progetto lo scrive esplicitamente.
 */
/**
 * Autenticazione dell'MCP per progetto.
 *
 * Esiste perche i client MCP remoti (claude.ai in testa) non si autenticano con
 * un token statico ma con OAuth: senza questo, l'endpoint remoto resta
 * raggiungibile solo da chi puo mettersi un Bearer a mano.
 *
 * Il server di autorizzazione e SEMPRE una impostazione — issuer, audience e
 * JWKS si configurano per progetto (o come default di tenant) e non compare
 * nessun endpoint cablato nel codice: piu Keycloak, o piu realm dello stesso
 * Keycloak, devono poter convivere nella stessa piattaforma.
 *
 * `mode: "token"` (il default) lascia tutto com'e: solo API token e sessioni.
 * `mode: "oauth"` AGGIUNGE la verifica dei JWT, non la sostituisce — un token
 * API che gia funziona continua a funzionare, altrimenti attivare l'OAuth
 * spegnerebbe le automazioni esistenti.
 */
export const McpAuthConfigSchema = z
    .object({
    mode: z.enum(["token", "oauth"]),
    /** Issuer atteso nel claim `iss`. Deve combaciare esattamente. */
    issuer: z.string().url().optional(),
    /** Audience attesa. Omessa = non verificata (sconsigliato). */
    audience: z.string().min(1).optional(),
    /**
     * JWKS. Se omesso si deriva da `issuer` con il percorso standard di
     * OpenID Connect Discovery; si specifica solo quando il server lo espone
     * altrove.
     */
    jwksUri: z.string().url().optional(),
    /** Scope OAuth richiesto nel token (claim `scope`). Vuoto = nessuno. */
    requiredScope: z.string().optional(),
    /**
     * NON esiste piu un claim configurabile per identificare l'utente.
     *
     * C'era, e apriva un'impersonazione: `email_verified` certifica il claim
     * `email` e nient'altro, mentre la ricerca dell'utente usava un claim
     * qualsiasi. Con `preferred_username` un token con la PROPRIA email
     * verificata e il `preferred_username` di un altro veniva mappato sulla
     * vittima. La corrispondenza avviene sempre su `email`, e solo con
     * `email_verified: true`.
     */
})
    .refine((c) => c.mode !== "oauth" || (c.audience !== undefined && c.audience !== ""), {
    // Senza audience un token che lo stesso IdP ha emesso per un ALTRO client
    // sarebbe accettato qui (RFC 8725 §3.9, token substitution).
    message: "audience is required when mode is 'oauth'",
    path: ["audience"],
})
    .refine((c) => c.mode !== "oauth" || (c.issuer !== undefined && c.issuer !== ""), {
    // Senza issuer non c'e niente contro cui verificare: accettare questa
    // configurazione significherebbe "OAuth attivo" e in realta nessun
    // controllo. Meglio rifiutare la scrittura.
    message: "issuer is required when mode is 'oauth'",
    path: ["issuer"],
});
export const ApiEnabledSchema = z.boolean();
export const McpEnabledSchema = z.boolean();
/**
 * Desktop `apiConfig` persisted shape — kept ONLY for parsing imported
 * desktop `app-state.json` files. Never persisted server-side.
 */
export const LegacyDesktopApiConfigSchema = z.object({
    enabled: z.boolean(),
    allowUnauthenticated: z.boolean(),
    mcpEnabled: z.boolean(),
    token: z.string(),
});
// ─── Scope registry ──────────────────────────────────────────────────────
export const ConfigScopeSchema = z.enum(["tenant", "user", "project"]);
/**
 * The full key registry, mirroring inventory 04 §7. Keys not listed in
 * `CONFIG_KEYS` are rejected by the configs API (fail-closed).
 */
export const CONFIG_KEYS = {
    // LLM / providers (desktop: global → server: tenant)
    llmConfig: {
        scopes: ["project", "tenant"],
        secret: true,
        forceFlush: false,
        schema: LlmConfigSchema,
    },
    providerConfigs: {
        // project override → tenant default (per-project provider keys, 29/09/2026)
        scopes: ["project", "tenant"],
        secret: true,
        forceFlush: false,
        schema: ProviderConfigsSchema,
    },
    activePresetId: {
        // project override → tenant default (per-project provider keys, 29/09/2026)
        scopes: ["project", "tenant"],
        secret: false,
        forceFlush: false,
        schema: z.string().nullable(),
    },
    searchApiConfig: {
        // project override → tenant default (per-project provider keys, 29/09/2026)
        scopes: ["project", "tenant"],
        secret: true,
        forceFlush: false,
        schema: SearchApiConfigSchema,
    },
    embeddingConfig: {
        // project override → tenant default (per-project provider keys, 29/09/2026)
        scopes: ["project", "tenant"],
        secret: true,
        forceFlush: true, // rustd reads it per search request
        schema: EmbeddingConfigSchema,
    },
    multimodalConfig: {
        // project override → tenant default (per-project provider keys, 29/09/2026)
        scopes: ["project", "tenant"],
        secret: true,
        forceFlush: false,
        schema: MultimodalConfigSchema,
    },
    mineruConfig: {
        // project override → tenant default (per-project provider keys, 29/09/2026)
        scopes: ["project", "tenant"],
        secret: true,
        forceFlush: false,
        schema: MineruConfigSchema,
    },
    proxyConfig: {
        scopes: ["tenant"],
        secret: true, // URL may embed basic-auth credentials
        forceFlush: true, // desktop force-flushed; server: NOTIFY on change
        schema: ProxyConfigSchema,
    },
    apiEnabled: {
        scopes: ["tenant"],
        secret: false,
        forceFlush: true,
        schema: ApiEnabledSchema,
    },
    mcpAuthConfig: {
        // SOLO progetto, di proposito. Un default di tenant significherebbe che
        // una scrittura sola estende la fiducia verso un IdP a tutte le wiki del
        // tenant, comprese quelle che non lo hanno mai chiesto: per una decisione
        // di autenticazione l'esplicito vale piu della comodita.
        scopes: ["project"],
        // Non contiene segreti (issuer/audience sono pubblici per definizione:
        // stanno nei token e nei metadati di discovery), quindi niente cifratura.
        secret: false,
        forceFlush: true,
        schema: McpAuthConfigSchema,
    },
    mcpEnabled: {
        // project = interruttore della singola wiki; tenant = kill-switch. In AND.
        scopes: ["project", "tenant"],
        secret: false,
        forceFlush: true,
        schema: McpEnabledSchema,
    },
    // Output language: tenant default + per-project override
    outputLanguage: {
        scopes: ["project", "tenant"],
        secret: false,
        forceFlush: false,
        schema: OutputLanguageSchema,
    },
    // Per-project lifecycle configs
    scheduledImportConfig: {
        scopes: ["project"],
        secret: false,
        forceFlush: false,
        schema: ScheduledImportConfigSchema,
    },
    sourceWatchConfig: {
        // project row overrides the tenant-level "default" row (desktop's
        // per-project map + `default` key semantics).
        scopes: ["project", "tenant"],
        secret: false,
        forceFlush: true, // rustd rescan endpoint reads it
        schema: SourceWatchConfigSchema,
    },
    // Git source connector — per-project repo list (metadata + refs only,
    // no plaintext tokens). forceFlush so a config change NOTIFYs the
    // runtime to reschedule interval syncs (mirrors scheduledImportConfig).
    gitSourceConfig: {
        scopes: ["project"],
        secret: false,
        forceFlush: true,
        schema: GitSourceConfigSchema,
    },
    // Git auth tokens, encrypted at rest (mirrors llmConfig secret path).
    gitSourceSecrets: {
        scopes: ["project"],
        secret: true,
        forceFlush: false,
        schema: GitSourceSecretsSchema,
    },
    watcherMode: {
        scopes: ["project", "tenant"],
        secret: false,
        forceFlush: true,
        schema: WatcherModeConfigSchema,
    },
    // User UI prefs
    language: {
        scopes: ["user"],
        secret: false,
        forceFlush: false,
        schema: UiLanguageSchema,
    },
    theme: {
        scopes: ["user"],
        secret: false,
        forceFlush: false,
        schema: ThemeSchema,
    },
    zoomLevel: {
        scopes: ["user"],
        secret: false,
        forceFlush: false,
        schema: ZoomLevelSchema,
    },
    maxHistoryMessages: {
        scopes: ["user", "tenant"],
        secret: false,
        forceFlush: false,
        schema: z.number().int().min(1).max(1000),
    },
};
export const ConfigKeySchema = z.enum(Object.keys(CONFIG_KEYS));
/**
 * Desktop keys with NO server config row — handled elsewhere or dropped.
 * Kept as a checklist for the desktop-project importer.
 */
export const LEGACY_CONFIG_KEYS = [
    "recentProjects", //         → user_project_state table
    "lastProject", //            → user_project_state table
    "projectRegistry", //        → projects table
    "apiConfig", //              → api_tokens table + apiEnabled/mcpEnabled
    "generalConfig", //          DROPPED (autostart/closeBehavior are desktop-only)
    "updateCheckState", //       DROPPED (F-189 update banner dropped)
    "projectFileSyncEnabled", // legacy boolean → migrated into sourceWatchConfig
    "projectOutputLanguages", // per-project map → project-scoped outputLanguage rows
    "scheduledImportConfig:", // path-keyed prefix keys → project-scoped rows
];
// ─── Config rows & API DTOs ──────────────────────────────────────────────
/**
 * A resolved config row (configs table, §8.4). `userId`/`projectId`
 * are null for tenant-scope rows. Secret values are returned REDACTED
 * by the public API (see `RedactedSchema` note below); the raw value
 * only travels on internal, X-Internal-Token-authenticated hops.
 */
export const ConfigRowSchema = z.object({
    tenantId: UuidSchema,
    userId: UuidSchema.nullable(),
    projectId: UuidSchema.nullable(),
    key: ConfigKeySchema,
    value: z.unknown(),
    updatedAt: z.string(),
});
/** Marker string substituted for secret fields on the public API. */
export const SECRET_REDACTED = "__redacted__";
/** `NOTIFY config_changed` payload (Postgres LISTEN/NOTIFY, ADR-003). */
export const ConfigChangedNotifySchema = z.object({
    tenantId: UuidSchema,
    userId: UuidSchema.nullable().optional(),
    projectId: UuidSchema.nullable().optional(),
    key: ConfigKeySchema,
});
/** Validate a value against its registered key schema. */
export function parseConfigValue(key, value) {
    return CONFIG_KEYS[key].schema.parse(value);
}
export function isSecretConfigKey(key) {
    return CONFIG_KEYS[key].secret;
}
export function configKeyScopes(key) {
    return CONFIG_KEYS[key].scopes;
}
//# sourceMappingURL=config.js.map